import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig, AxiosRequestConfig } from 'axios';
import type { ApiResponse, TaskGroupCopy, Attachment } from '../types';
import { websocketService } from './websocket';

const API_URL = import.meta.env.VITE_API_URL || '/api';

export const api: AxiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('accessToken');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Send the user's selected language to the API so server messages are localized.
    if (config.headers) {
      const lang = localStorage.getItem('taskfly-language') || navigator.language || 'es';
      config.headers['Accept-Language'] = lang.split('-')[0];
    }
    // When uploading FormData, let the browser set multipart/form-data with the
    // correct boundary. Remove any pre-set Content-Type header in that case.
    if (config.data instanceof FormData && config.headers) {
      delete config.headers['Content-Type'];
      delete (config.headers as any)['content-type'];
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle token refresh and error extraction
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiResponse<any>>) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    
    // Log error for debugging (only in development)
    if (import.meta.env.DEV) {
      console.error('[API Error]', {
        status: error.response?.status,
        code: error.response?.data?.error?.code,
        message: error.response?.data?.error?.message,
        details: error.response?.data?.error?.details,
      });
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        try {
          const { data } = await axios.post(`${API_URL}/auth/refresh`, { refreshToken });
          localStorage.setItem('accessToken', data.data.accessToken);
          localStorage.setItem('refreshToken', data.data.refreshToken);
          
          // REV-F1: Reconnect WebSocket with new token after refresh
          try {
            websocketService.connect(data.data.accessToken);
          } catch (wsErr) {
            console.warn('[API] Failed to reconnect WebSocket after token refresh:', wsErr);
          }
          
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${data.data.accessToken}`;
          }
          return api(originalRequest);
        } catch {
          // Refresh failed, logout
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          window.location.href = '/app/login';
        }
      }
    }

    return Promise.reject(error);
  }
);

// API helper functions with AbortSignal support (REV-9: request cancellation)
export async function get<T>(
  url: string, 
  params?: Record<string, unknown>, 
  options?: AxiosRequestConfig
): Promise<ApiResponse<T>> {
  const { data } = await api.get<ApiResponse<T>>(url, { 
    params,
    ...options 
  });
  return data;
}

export async function post<T>(url: string, body?: unknown, options?: AxiosRequestConfig): Promise<ApiResponse<T>> {
  const { data } = await api.post<ApiResponse<T>>(url, body, options);
  return data;
}

export async function put<T>(url: string, body?: unknown, options?: AxiosRequestConfig): Promise<ApiResponse<T>> {
  const { data } = await api.put<ApiResponse<T>>(url, body, options);
  return data;
}

export async function patch<T>(url: string, body?: unknown, options?: AxiosRequestConfig): Promise<ApiResponse<T>> {
  const { data } = await api.patch<ApiResponse<T>>(url, body, options);
  return data;
}

export async function del<T>(url: string, options?: AxiosRequestConfig): Promise<ApiResponse<T>> {
  const { data } = await api.delete<ApiResponse<T>>(url, options);
  return data;
}

// Auth API
export const authApi = {
  login: (email: string, password: string, captchaToken?: string) => 
    post('/auth/login', { email, password, captchaToken }),
  register: (data: { email: string; password: string; firstName: string; lastName: string; captchaToken?: string }) => 
    post('/auth/register', { ...data, captchaToken: data.captchaToken }),
  me: () => get('/auth/me'),
  logout: () => {
    const refreshToken = localStorage.getItem('refreshToken');
    return post('/auth/logout', { refreshToken });
  },
  refresh: (refreshToken: string) => post('/auth/refresh', { refreshToken }),
};

// User API
export const userApi = {
  getProfile: () => get('/users/profile'),
  updateProfile: (data: Record<string, unknown>) => put('/users/profile', data),
  updatePassword: (currentPassword: string, newPassword: string) => 
    put('/users/password', { currentPassword, newPassword }),
  linkTelegram: (chatId: string) => post('/users/telegram/link', { chatId }),
  unlinkTelegram: () => del('/users/telegram/unlink'),
  getUsers: (params?: Record<string, unknown>) => get('/users', params),
  getUsersList: () => get('/users/list'),
  getUsersListNames: () => get('/users/list/names'),
  updateUserRole: (userId: string, role: string) => put(`/users/${userId}/role`, { role }),
};

// Tasks API
export const taskApi = {
  list: (params?: Record<string, unknown>) => get('/tasks', params),
  getHistory: (params?: Record<string, unknown>) => get('/tasks/history', params),
  get: (id: string) => get(`/tasks/${id}`),
  create: (data: Record<string, unknown>) => post('/tasks', data),
  update: (id: string, data: Record<string, unknown>) => put(`/tasks/${id}`, data),
  delete: (id: string) => del(`/tasks/${id}`),
  complete: (id: string) => patch(`/tasks/${id}/complete`, {}),
  assignToMe: (id: string) => post(`/tasks/${id}/assign-me`),
  createSubtask: (taskId: string, title: string) => post(`/tasks/${taskId}/subtasks`, { title }),
  updateSubtask: (id: string, data: Record<string, unknown>) => put(`/tasks/subtasks/${id}`, data),
  deleteSubtask: (id: string) => del(`/tasks/subtasks/${id}`),
  // Recurring task endpoints
  getRecurringTemplates: () => get('/tasks/recurring'),
  getRecurringTemplate: (id: string) => get(`/tasks/recurring/${id}`),
  pauseRecurring: (id: string) => patch(`/tasks/recurring/${id}/pause`, {}),
  resumeRecurring: (id: string) => patch(`/tasks/recurring/${id}/resume`, {}),
  deleteRecurringTemplate: (id: string, deleteInstances: boolean = false) => 
    del(`/tasks/recurring/${id}?deleteInstances=${deleteInstances}`),
  getRecurringInstances: (id: string, params?: Record<string, unknown>) => 
    get(`/tasks/recurring/${id}/instances`, params),
  // Individual task group copies — creator sees copies of other members
  getGroupCopies: (taskId: string) => get<TaskGroupCopy[]>(`/tasks/${taskId}/group-copies`),
};

// Projects API
export const projectApi = {
  list: (params?: Record<string, unknown>) => get('/projects', params),
  get: (id: string) => get(`/projects/${id}`),
  create: (data: Record<string, unknown>) => post('/projects', data),
  update: (id: string, data: Record<string, unknown>) => put(`/projects/${id}`, data),
  delete: (id: string) => del(`/projects/${id}`),
  archive: (id: string) => patch(`/projects/${id}/archive`, {}),
};

// Project Members API (Feature 6)
export const projectMemberApi = {
  list: (projectId: string) => get(`/projects/${projectId}/members`),
  getSummary: (projectId: string) => get(`/projects/${projectId}/members/summary`),
  add: (projectId: string, data: { userId: string; role?: string }) => 
    post(`/projects/${projectId}/members`, data),
  bulkAdd: (projectId: string, members: Array<{ userId: string; role?: string }>) => 
    post(`/projects/${projectId}/members/bulk`, { members }),
  updateRole: (projectId: string, userId: string, role: string) => 
    patch(`/projects/${projectId}/members/${userId}`, { role }),
  remove: (projectId: string, userId: string) => 
    del(`/projects/${projectId}/members/${userId}`),
  getMyProjects: () => get('/users/me/projects'),
};

// Time Entries API
export const timeEntryApi = {
  list: (params?: Record<string, unknown>) => get('/time-entries', params),
  getActive: () => get('/time-entries/active'),
  getToday: () => get('/time-entries/today'),
  getSummary: (params?: Record<string, unknown>) => get('/time-entries/summary', params),
  checkIn: (data: Record<string, unknown>) => post('/time-entries/check-in', data),
  checkOut: (data?: Record<string, unknown>) => post('/time-entries/check-out', data),
  // Admin endpoints
  getAllAttendance: (params?: Record<string, unknown>) => get('/time-entries/admin/attendance', params),
  getUserAttendance: (userId: string, params?: Record<string, unknown>) => get(`/time-entries/admin/attendance/${userId}`, params),
  exportAttendanceXlsx: async (params: {
    month: string;
    userIds?: string[];
    includeCheckInNotes?: boolean;
    includeCheckOutNotes?: boolean;
    includeLogs?: boolean;
  }) => {
    const query: Record<string, unknown> = { month: params.month, _t: Date.now() };
    if (params.userIds && params.userIds.length > 0) {
      query.userIds = params.userIds.join(',');
    }
    if (params.includeCheckInNotes === false) query.includeCheckInNotes = 'false';
    if (params.includeCheckOutNotes === false) query.includeCheckOutNotes = 'false';
    if (params.includeLogs === false) query.includeLogs = 'false';
    const response = await api.get('/time-entries/export/xlsx', {
      params: query,
      responseType: 'blob',
      headers: { 'Cache-Control': 'no-cache' },
    });
    return response;
  },
};

// Labels API
export const labelApi = {
  list: () => get('/labels'),
  create: (data: Record<string, unknown>) => post('/labels', data),
  update: (id: string, data: Record<string, unknown>) => put(`/labels/${id}`, data),
  delete: (id: string) => del(`/labels/${id}`),
};

// Notifications API
export const notificationApi = {
  list: (params?: Record<string, unknown>) => get('/notifications', params),
  getUnreadCount: () => get('/notifications/unread-count'),
  getBadgeCounts: () => get('/notifications/badge-counts'),
  getOrgCounts: () => get('/notifications/org-counts'),
  markAsRead: (id: string) => put(`/notifications/${id}/read`, {}),
  markAllAsRead: () => put('/notifications/read-all', {}),
  delete: (id: string) => del(`/notifications/${id}`),
};

// Attachments API
export const attachmentApi = {
  uploadTempAttachment: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return post<Attachment>('/attachments/upload', formData);
  },
  list: (taskId: string) => get<Attachment[]>(`/tasks/${taskId}/attachments`),
  delete: (id: string) => del(`/attachments/${id}`),
};

// Organization API
export const organizationApi = {
  list: () => get('/organizations'),
  get: (id: string) => get(`/organizations/${id}`),
  create: (name: string) => post('/organizations', { name }),
  update: (id: string, data: Record<string, unknown>) => put(`/organizations/${id}`, data),
  delete: (id: string) => del(`/organizations/${id}`),
  getMembers: (id: string) => get(`/organizations/${id}/members`),
  removeMember: (orgId: string, userId: string) => del(`/organizations/${orgId}/members/${userId}`),
  updateMemberRole: (orgId: string, userId: string, role: string) => 
    put(`/organizations/${orgId}/members/${userId}/role`, { role }),
  leave: (orgId: string) => post(`/organizations/${orgId}/leave`),
  setActive: (orgId: string) => put('/users/me/active-organization', { orgId }),
  // Join requests
  getMyRequests: () => get('/organization-join-requests/my-requests'),
  requestJoin: (orgId: string) => post('/organization-join-requests', { orgId }),
  cancelRequest: (requestId: string) => del(`/organization-join-requests/${requestId}`),
  getPendingRequests: (orgId: string) => get(`/organization-join-requests/org/${orgId}/pending`),
  resolveRequest: (orgId: string, requestId: string, status: 'APPROVED' | 'REJECTED') =>
    put(`/organization-join-requests/org/${orgId}/requests/${requestId}/resolve`, { status }),
  // Lookup
  lookup: (uuid: string) => get('/organizations/lookup', { uuid }),
};

// User Notification State API (Server-side badge tracking)
export const userNotificationStateApi = {
  /** Get all notification states for the current user */
  getStates: () => get('/user-notification-state'),
  /** Mark a page as seen (clears badge) - action: markSeen (default) */
  markPageSeen: (pageType: string) => {
    return post(`/user-notification-state/${pageType}`, { action: 'markSeen' });
  },
  /** Clear badge without marking page as seen */
  clearBadge: (pageType: string) => post(`/user-notification-state/${pageType}`, { action: 'clearBadge' }),
  /** Reset badge state (show all pending items again) */
  resetBadge: (pageType: string) => post(`/user-notification-state/${pageType}`, { action: 'resetBadge' }),
  /** Clear all badges for all page types */
  clearAll: () => post('/user-notification-state/clear-all', {}),
};

// Reminders API
export const reminderApi = {
  list: (params?: Record<string, unknown>) => get('/reminders', params),
  get: (id: string) => get(`/reminders/${id}`),
  create: (data: Record<string, unknown>) => post('/reminders', data),
  update: (id: string, data: Record<string, unknown>) => put(`/reminders/${id}`, data),
  delete: (id: string) => del(`/reminders/${id}`),
  complete: (id: string) => post(`/reminders/${id}/complete`, {}),
  snooze: (id: string, minutes: number) => post(`/reminders/${id}/snooze`, { minutes }),
  pause: (id: string) => post(`/reminders/${id}/pause`, {}),
  resume: (id: string) => post(`/reminders/${id}/resume`, {}),
  getUpcoming: (days?: number) => get('/reminders/upcoming', { days }),
};

// Statistics API (Admin only)
export const statisticsApi = {
  getGeneral: (params?: Record<string, unknown>, options?: AxiosRequestConfig) => get('/statistics', params, options),
  getUsers: (params?: Record<string, unknown>, options?: AxiosRequestConfig) => get('/statistics/users', params, options),
  getUser: (userId: string, params?: Record<string, unknown>, options?: AxiosRequestConfig) => get(`/statistics/users/${userId}`, params, options),
  getDaily: (params?: Record<string, unknown>, options?: AxiosRequestConfig) => get('/statistics/daily', params, options),
};

export default api;